// 선거 윈도우 관리 — 05-admin §4.3 (공직선거법 82조의8).
// GET/POST/DELETE /v1/admin/election-windows (01 §3.M, admin).
// election_windows 테이블(01 §2): region / name / starts_at(90일 전) / ends_at(선거일).
import { api, errMsg, isNotReady } from "../core/api.js";
import { h, mount, notReadyState } from "../core/dom.js";
import type { ElectionWindow, Page } from "../models.js";

function fmtDate(ms: number): string {
  return new Date(ms).toLocaleDateString("ko-KR");
}

// 윈도우 활성 상태(현재가 [starts_at, ends_at] 사이인가) 배지.
function windowBadge(w: ElectionWindow): HTMLElement {
  const now = Date.now();
  if (now < w.starts_at) {
    const d = Math.ceil((w.starts_at - now) / 86_400_000);
    return h("span", { class: "badge" }, `예정 (D-${d})`);
  }
  if (now > w.ends_at) return h("span", { class: "badge muted" }, "종료");
  const d = Math.ceil((w.ends_at - now) / 86_400_000);
  return h("span", { class: "badge danger" }, `활성 — 차단 강화 (선거일 D-${d})`);
}

export async function electionsView(_p: Record<string, string>, root: HTMLElement): Promise<void> {
  const listWrap = h("div", { class: "card" });
  const formStatus = h("p", { class: "small muted" }, "");

  const regionField = h("input", { class: "field", placeholder: "region (예: KR)" }) as HTMLInputElement;
  const nameField = h("input", { class: "field", placeholder: "명칭 (예: 제22대 국회의원선거)" }) as HTMLInputElement;
  const startField = h("input", { class: "field", type: "date" }) as HTMLInputElement;
  const endField = h("input", { class: "field", type: "date" }) as HTMLInputElement;

  async function create(): Promise<void> {
    const region = regionField.value.trim();
    const name = nameField.value.trim();
    if (!region || !name || !startField.value || !endField.value) {
      formStatus.textContent = "region / 명칭 / 시작(90일 전) / 선거일을 모두 입력하세요.";
      formStatus.className = "small error";
      return;
    }
    const starts_at = new Date(startField.value + "T00:00:00Z").getTime();
    const ends_at = new Date(endField.value + "T23:59:59Z").getTime();
    if (ends_at <= starts_at) {
      formStatus.textContent = "선거일은 시작일보다 뒤여야 합니다.";
      formStatus.className = "small error";
      return;
    }
    formStatus.textContent = "등록 중…";
    formStatus.className = "small muted";
    try {
      await api.post("/v1/admin/election-windows", { region, name, starts_at, ends_at });
      formStatus.textContent = "선거 윈도우 등록됨 (감사 로그 기록).";
      formStatus.className = "small ok";
      regionField.value = nameField.value = startField.value = endField.value = "";
      void load();
    } catch (e) {
      formStatus.textContent = isNotReady(e)
        ? "엔드포인트 준비중 (/v1/admin/election-windows POST 미구현)."
        : `등록 실패: ${errMsg(e)}`;
      formStatus.className = "small error";
    }
  }

  async function remove(w: ElectionWindow): Promise<void> {
    if (!window.confirm(`선거 윈도우 "${w.name}"을(를) 삭제할까요? (감사 로그 기록)`)) return;
    try {
      await api.del(`/v1/admin/election-windows/${w.id}`);
      void load();
    } catch (e) {
      formStatus.textContent = isNotReady(e)
        ? "엔드포인트 준비중 (DELETE 미구현)."
        : `삭제 실패: ${errMsg(e)}`;
      formStatus.className = "small error";
    }
  }

  async function load(): Promise<void> {
    listWrap.replaceChildren(h("div", { class: "state muted" }, "선거 윈도우 불러오는 중…"));
    try {
      const page = await api.get<Page<ElectionWindow> | ElectionWindow[]>("/v1/admin/election-windows");
      const items = Array.isArray(page) ? page : (page.items ?? []);
      if (items.length === 0) {
        listWrap.replaceChildren(h("div", { class: "state muted" }, "등록된 선거 윈도우가 없습니다."));
        return;
      }
      const table = h(
        "table",
        { class: "tbl" },
        h(
          "thead",
          {},
          h(
            "tr",
            {},
            h("th", {}, "region"),
            h("th", {}, "명칭"),
            h("th", {}, "시작 (90일 전)"),
            h("th", {}, "선거일"),
            h("th", {}, "상태"),
            h("th", {}, ""),
          ),
        ),
        h(
          "tbody",
          {},
          ...items.map((w) =>
            h(
              "tr",
              {},
              h("td", {}, h("span", { class: "badge" }, w.region)),
              h("td", {}, w.name),
              h("td", { class: "muted small" }, fmtDate(w.starts_at)),
              h("td", { class: "muted small" }, fmtDate(w.ends_at)),
              h("td", {}, windowBadge(w)),
              h("td", {}, h("button", { class: "btn small danger", onclick: () => void remove(w) }, "삭제")),
            ),
          ),
        ),
      );
      listWrap.replaceChildren(table);
    } catch (e) {
      if (isNotReady(e)) {
        listWrap.replaceChildren(notReadyState("선거 윈도우 목록 (/v1/admin/election-windows) 미구현."));
        return;
      }
      listWrap.replaceChildren(h("div", { class: "state error" }, `불러오기 실패: ${errMsg(e)}`));
    }
  }

  mount(
    root,
    h(
      "div",
      { class: "stack" },
      h("h1", {}, "선거 윈도우 관리"),
      h(
        "p",
        { class: "muted" },
        "선거일 전 90일~선거일 구간 딥페이크 선거운동 전면 금지 (05-admin §4.3). 활성 윈도우 동안 q.moderation이 정치 딥페이크 의심 콘텐츠를 보수적 차단/경고합니다. 등록/삭제는 admin 전용 → 감사 로그.",
      ),
      h(
        "section",
        { class: "card" },
        h("h2", {}, "윈도우 등록 (admin)"),
        h("div", { class: "row wrap" }, regionField, nameField),
        h(
          "div",
          { class: "row wrap" },
          h("label", { class: "lbl" }, "시작(90일 전)"),
          startField,
          h("label", { class: "lbl" }, "선거일"),
          endField,
        ),
        h("div", { class: "row" }, h("button", { class: "btn", onclick: () => void create() }, "등록")),
        formStatus,
      ),
      listWrap,
    ),
  );
  await load();
}
